import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  liveParamsFromSearch,
  pathLabel,
  productFromEnvelope,
  productMatchesLiveParams,
  isDemoSearch,
  isAnalyzerLaunchSearch,
} from "@/lib/analyzeQuery";
import {
  historyEntryFromProduct,
  rememberAnalyzerLookup,
} from "@/lib/analyzerLaunchHistory";
import { computeCalc, computeVerdict, computeRoiScenarios, usd, pct, num, ago } from "@/lib/format";
import {
  demandTooltip,
  formatMoneyRange,
  formatUnitsRange,
  revenueTooltip,
} from "@/lib/demandEstimate";
import {
  buildCurrentRangeCompetitionRows,
} from "@/lib/currentRangeCompetition";
import { CurrentRangeCompetitionTable } from "@/components/analyzer/CurrentRangeCompetitionTable";
import { BuySourcesPanel } from "@/components/analyzer/BuySourcesPanel";
import { buildBuySourcesSearchBody } from "@/lib/buySources/buildSearchRequest";
import { parseBuySourcesSearchResponse } from "@/lib/buySources/parseSearchResponse";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { MarketHistory } from "@/components/analyzer/MarketHistory";
import { WalmartDataTab } from "@/components/analyzer/WalmartDataTab";
import { WalmartVariationsTab } from "@/components/analyzer/WalmartVariationsTab";
import { WalmartProductDetailsTab } from "@/components/analyzer/WalmartProductDetailsTab";
import { normalizeAnalyzerTab, comparisonColumnForPeriod } from "@/lib/analyzerData";
import { MatchHeader } from "@/components/analyzer/MatchHeader";
import { ProfitCalculator } from "@/components/analyzer/ProfitCalculator";
import ProductLookupLoader from "@/components/analyzer/ProductLookupLoader";
import AnalyzerLaunch from "@/components/analyzer/AnalyzerLaunch";
import {
  Panel, ProvenanceTag, VerdictBadge, verdictStyle, StatusBadge,
} from "@/components/common/primitives";
import {
  AlertTriangle, HelpCircle,
  Bookmark, X, Eye, ListPlus, MoreHorizontal, MinusCircle,
  LayoutGrid, LineChart, Store, Activity, GitBranch, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { saveStorefrontProduct } from "@/lib/storefrontHunter/api";
import { normalizeSavedItemId } from "@/lib/storefrontHunter/savedProducts";

const STATUS_BADGE = {
  pass: { tone: "success", label: "Passed" },
  fail: { tone: "danger", label: "Failed" },
  warning: { tone: "warning", label: "Needs review" },
  unknown: { tone: "neutral", label: "Unknown" },
};
function StatusPill({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.unknown;
  return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
}

function Card({ title, children, testid, badge = false, right, className }) {
  return (
    <Panel className={cn("p-4 min-w-0 self-start", className)} testid={testid}>
      {title && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-stitle text-ink-primary flex items-center gap-2">{title}{badge && <ProvenanceTag kind="fixture" />}</div>
          {right}
        </div>
      )}
      {children}
    </Panel>
  );
}

function EstimateMetricCard({
  heading,
  value,
  caption,
  tip,
  testid,
}) {
  return (
    <Panel className="p-4 min-w-0" testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-micro font-mono uppercase tracking-wide text-ink-muted">{heading}</div>
        <StatusBadge tone="warning">Estimated</StatusBadge>
      </div>
      <div className="flex items-baseline gap-2 min-w-0">
        <div className="text-[22px] font-semibold tabnum text-ink-primary truncate">{value}</div>
        {tip && (
          <span title={tip} tabIndex={0} aria-label={tip} className="cursor-help text-ink-muted shrink-0">
            <HelpCircle className="h-3.5 w-3.5" aria-hidden />
          </span>
        )}
      </div>
      <div className="text-compact text-ink-secondary mt-1">{caption}</div>
    </Panel>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "history", label: "History", icon: LineChart },
  { key: "sellers", label: "Sellers & Stock", icon: Store },
  { key: "data", label: "Data", icon: Activity },
  { key: "variations", label: "Variations", icon: GitBranch },
  { key: "details", label: "Product Details", icon: FileText },
];

function fmtCA(v, unit) {
  if (v === null || v === undefined) return "—";
  if (unit === "money") return usd(v);
  return typeof v === "number" ? num(Math.round(v * 10) / 10) : v;
}

/**
 * Shell remounts analyzer state whenever location.search changes so a prior
 * product can never stay mounted while a new Analyze request is in flight.
 * Bare /app/analyzer shows the launch landing — demo is opt-in via ?demo=1.
 */
export default function ProductAnalyzer() {
  const location = useLocation();
  const searchKey = location.search || "";
  if (isAnalyzerLaunchSearch(searchKey)) {
    return <AnalyzerLaunch />;
  }
  return <ProductAnalyzerView key={searchKey || "demo"} searchKey={searchKey} />;
}

function ProductAnalyzerView({ searchKey }) {
  const nav = useNavigate();
  const liveParams = useMemo(() => liveParamsFromSearch(searchKey), [searchKey]);
  const isDemoRequest = useMemo(() => isDemoSearch(searchKey), [searchKey]);
  const isLiveRequest = Boolean(liveParams);

  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [input, setInput] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dataMode, setDataMode] = useState(null);
  const [lookupPath, setLookupPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(false);
  const [showLogic, setShowLogic] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [fulfillment, setFulfillment] = useState("wfs");
  const [hideZero, setHideZero] = useState(false);
  const [offerFilter, setOfferFilter] = useState("all");
  const [confirmReject, setConfirmReject] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tab, setTabRaw] = useState("overview");
  const setTab = useCallback((v) => setTabRaw(normalizeAnalyzerTab(v)), []);
  const [panelOpen, setPanelOpen] = useState(true);
  const tabListRef = useRef(null);
  const tabBtnRefs = useRef({});
  const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0 });
  const [buySourcesResult, setBuySourcesResult] = useState(null);
  const [buySourcesLoading, setBuySourcesLoading] = useState(false);
  const [buySourcesSearched, setBuySourcesSearched] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  // Monotonic id so out-of-order product responses never win (Strict Mode / rapid remounts).
  const requestIdRef = useRef(0);

  const measureTabIndicator = useCallback(() => {
    const list = tabListRef.current;
    const btn = tabBtnRefs.current[tab];
    if (!list || !btn) return;
    setTabIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [tab]);

  useLayoutEffect(() => {
    measureTabIndicator();
    const list = tabListRef.current;
    const btn = tabBtnRefs.current[tab];
    if (!list || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureTabIndicator);
      return () => window.removeEventListener("resize", measureTabIndicator);
    }
    const ro = new ResizeObserver(() => measureTabIndicator());
    ro.observe(list);
    if (btn) ro.observe(btn);
    window.addEventListener("resize", measureTabIndicator);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureTabIndicator);
    };
  }, [tab, measureTabIndicator, panelOpen]);

  useEffect(() => {
    // Drop previous product immediately — never keep prior product while looking up.
    setProduct(null);
    setInput(null);
    setLoadError(null);
    setDataMode(null);
    setLookupPath(null);
    setHistory(null);
    setBundle(null);
    setBuySourcesResult(null);
    setBuySourcesSearched(false);
    setBuySourcesLoading(false);
    setLoading(true);

    const requestId = ++requestIdRef.current;
    const params = liveParamsFromSearch(searchKey);
    const isCurrent = () => requestId === requestIdRef.current;

    const req = params ? api.product(params) : api.product();
    req
      .then((r) => {
        if (!isCurrent()) return;
        const parsed = productFromEnvelope(r);
        if (!parsed) {
          setProduct(null);
          setInput(null);
          setDataMode(r?.data_mode || null);
          setLookupPath(null);
          setLoadError("Product not found in catalog response.");
          setLoading(false);
          return;
        }
        if (parsed.resolved === false || !parsed.product) {
          setProduct(null);
          setInput(null);
          setDataMode(parsed.dataMode || "live");
          setLookupPath(parsed.path || null);
          setLoadError(
            parsed.message ||
              parsed.reason ||
              "No Walmart catalog match for this identifier."
          );
          setLoading(false);
          return;
        }
        if (!productMatchesLiveParams(parsed.product, params)) {
          setProduct(null);
          setInput(null);
          setDataMode(parsed.dataMode);
          setLookupPath(parsed.path || null);
          setLoadError(
            `Catalog response did not match itemId=${params.itemId}. Try again or paste a Walmart product URL.`
          );
          setLoading(false);
          toast.error("Catalog response did not match the searched item id");
          return;
        }
        setDataMode(parsed.dataMode);
        setLookupPath(parsed.path || null);
        setProduct(parsed.product);
        setInput(parsed.product.calc_input);
        setLoadError(null);
        setLoading(false);
        if (params) {
          const entry = historyEntryFromProduct(parsed.product);
          if (entry) rememberAnalyzerLookup(entry);
        }
        // Reload history after product so the observation just recorded is included.
        api.history(params || {}).then((hr) => { if (isCurrent()) setHistory(hr.data); }).catch(() => {
          if (isCurrent() && params) {
            setHistory({
              series: [],
              available: false,
              coverage: "empty",
              reason: "no_observations",
              observation_count: 0,
              message: "Could not load market history.",
            });
          }
        });
      })
      .catch((err) => {
        if (!isCurrent()) return;
        const detail = err?.response?.data?.detail;
        const status = err?.response?.status;
        const msg = typeof detail === "string"
          ? detail
          : detail?.message || err?.message || "Failed to load product";
        setProduct(null);
        setInput(null);
        setDataMode(null);
        setLookupPath(typeof detail === "object" ? detail?.path || null : null);
        setLoadError(status === 404 ? (msg || "Product not found") : msg);
        setLoading(false);
        toast.error(msg);
      });

    // Persisted history is a read-only MongoDB read and is independent of the
    // live catalog call, so load it immediately: the graph renders from saved
    // observations even when the product lookup fails or is rate-limited.
    api.history(params || {}).then((r) => { if (isCurrent()) setHistory(r.data); }).catch(() => {});
    api.bundle(params || {}).then((r) => { if (isCurrent()) setBundle(r.data); }).catch(() => {
      if (isCurrent() && params) {
        setBundle({
          available: false,
          offers: [],
          eligibility: [],
          variations: [],
          current_average: { columns: [], rows: [] },
          product_details: [],
          offers_reason: "offers_unavailable",
          message: "Could not load analyzer tabs.",
        });
      }
    });

    return () => {
      // Invalidate this generation so a late response cannot write into a new search.
      if (requestIdRef.current === requestId) requestIdRef.current += 1;
    };
  }, [searchKey]);

  const calc = useMemo(() => (input ? computeCalc(input) : null), [input]);
  const verdict = useMemo(() => (input && calc ? computeVerdict(input, calc) : null), [input, calc]);
  useEffect(() => { if (calc) { setFlash(true); const t = setTimeout(() => setFlash(false), 620); return () => clearTimeout(t); } }, [calc?.net_profit]);

  const saveLead = useCallback(async (overrideItemId) => {
    if (!product || savingLead) return;
    const wm = product.walmart || {};
    const src = product.source || {};
    const rawId =
      overrideItemId ||
      wm.walmart_item_id ||
      wm.item_id_clean ||
      product.id ||
      src.source_id;
    const itemId = normalizeSavedItemId(rawId);
    if (!itemId) {
      toast.error("Cannot save — missing Walmart item id.");
      return;
    }
    setSavingLead(true);
    try {
      await saveStorefrontProduct({
        walmart_item_id: itemId,
        title: wm.title || src.title || product.title || null,
        image_url: (product.images && product.images[0]) || src.image || null,
        brand: wm.brand || src.brand || null,
        category: product.category || null,
        storefront_price:
          wm.price != null ? Number(wm.price) : src.price != null ? Number(src.price) : null,
        buy_box_price: wm.price != null ? Number(wm.price) : null,
      });
      toast.success("Lead saved to your workspace.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : err?.message || "Failed to save lead",
      );
    } finally {
      setSavingLead(false);
    }
  }, [product, savingLead]);

  const setField = useCallback((k, v) => {
    setInput((p) => {
      const next = { ...p, [k]: v };
      if (k === "acquisition_cost") {
        if (v == null || v === "" || Number(v) <= 0) {
          next.acquisition_cost_provenance = "missing";
          next.acquisition_cost_source = null;
          next.rule_source = p.data_mode === "live" ? "live" : p.rule_source;
        } else if (p.acquisition_cost_provenance === "price_index" && Number(v) === Number(p.acquisition_cost)) {
          // keep price_index when re-setting same value
        } else if (p.acquisition_cost_provenance === "price_index" && Number(v) !== Number(p.acquisition_cost)) {
          next.acquisition_cost_provenance = "user-entered";
          next.acquisition_cost_source = null;
          next.rule_source = "user-entered";
        } else if (p.acquisition_cost_provenance !== "price_index") {
          next.acquisition_cost_provenance = "user-entered";
          next.rule_source = p.data_mode === "live" ? "user-entered" : (p.rule_source || "fixture");
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const lock = showLogic || confirmReject;
    document.body.style.overflow = lock ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showLogic, confirmReject]);

  useEffect(() => {
    if (!showLogic && !confirmReject) return;
    const onKey = (e) => { if (e.key === "Escape") { setShowLogic(false); setConfirmReject(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLogic, confirmReject]);

  const isLiveCatalogPreview = dataMode === "live" || product?.data_quality?.catalog_source === "live";
  const hasLiveBundleDataPreview =
    Boolean(bundle?.available) &&
    (
      (bundle?.offers || []).length > 0 ||
      (bundle?.product_details || []).length > 0 ||
      (bundle?.variations || []).length > 0 ||
      (bundle?.current_average?.rows || []).length > 0
    );
  const bundleLiveEmptyPreview = bundle?.available === false || (isLiveCatalogPreview && !hasLiveBundleDataPreview);
  const caRows = useMemo(
    () => (bundleLiveEmptyPreview || !product ? [] : buildCurrentRangeCompetitionRows(bundle, product)),
    [bundleLiveEmptyPreview, bundle, product],
  );

  const handleFindBuySources = useCallback(async (postalCode) => {
    if (!product) return;
    setBuySourcesLoading(true);
    try {
      const body = buildBuySourcesSearchBody(product, postalCode);
      const res = await api.buySourcesSearch(body);
      setBuySourcesResult(parseBuySourcesSearchResponse(res));
      setBuySourcesSearched(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Buy sources search failed");
      setBuySourcesResult(null);
      setBuySourcesSearched(true);
    } finally {
      setBuySourcesLoading(false);
    }
  }, [product]);

  if (loadError && !product) {
    const notFound = /not found|no walmart catalog match|not listed|no catalog match/i.test(loadError);
    const pathLine = pathLabel(lookupPath);
    return (
      <div className="space-y-4" data-testid={notFound ? "analyzer-not-found" : "analyzer-load-error"}>
        <PageHeader
          title="Product Analyzer"
          description={notFound ? "No product matched this identifier." : "Could not load catalog data for this identifier."}
        />
        <Panel className="p-5 border border-red-200 bg-red-50 text-red-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-stitle font-semibold">{notFound ? "Product not found" : "Catalog lookup failed"}</div>
              <p className="text-compact mt-1 break-words">{loadError}</p>
              {pathLine && (
                <p className="text-micro mt-2 font-mono text-red-800/90" data-testid="analyzer-lookup-path">
                  Path: {pathLine}
                </p>
              )}
              <p className="text-micro mt-2 text-red-700/80">
                Try a Walmart item id (e.g. 18098957000), a product URL, or a UPC/GTIN with a valid check digit. Demo data is optional via “Explore with demo data” on the analyzer home.
              </p>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  if (loading || !product || !calc || !verdict) {
    return (
      <ProductLookupLoader
        live={isLiveRequest}
        onCancel={() => nav("/app/analyzer")}
      />
    );
  }

  const isLiveCatalog = dataMode === "live" || product?.data_quality?.catalog_source === "live";
  // Live tabs may still have Unwrangle PDP data (seller, snapshot, variations).
  // Only force-empty when the bundle itself is unavailable or still loading.
  const hasLiveBundleData =
    Boolean(bundle?.available) &&
    (
      (bundle?.offers || []).length > 0 ||
      (bundle?.product_details || []).length > 0 ||
      (bundle?.variations || []).length > 0 ||
      (bundle?.current_average?.rows || []).length > 0
    );
  const bundleLiveEmpty = bundle?.available === false || (isLiveCatalog && !hasLiveBundleData);
  const vs = verdictStyle(verdict.verdict);
  const m = product.match;
  const changeFulfillment = (v) => {
    setFulfillment(v);
    if (v === "seller") setField("wfs_fee", 0);
    else if (v === "wfs" && (input.wfs_fee ?? 0) === 0) setField("wfs_fee", 3.45);
  };
  const offers = bundleLiveEmpty
    ? []
    : (bundle?.offers || []).filter((o) =>
    offerFilter === "all" ? true :
    offerFilter === "wfs" ? o.fulfillment === "WFS" || o.fulfillment === "Likely WFS" || ["WFS", "LIKELY_WFS"].includes(o.fulfillment_classification?.type) :
    offerFilter === "sf" ? o.fulfillment === "Seller Fulfilled" || o.fulfillment === "Likely seller fulfilled" || ["SELLER_FULFILLED", "LIKELY_SELLER_FULFILLED"].includes(o.fulfillment_classification?.type) :
    offerFilter === "retail" ? o.fulfillment === "Walmart Retail" || o.fulfillment_classification?.type === "WALMART_RETAIL" : true);

  return (
    <div className="min-w-0 max-w-full pb-28 md:pb-24">
      <PageHeader
        title="Product Analyzer"
        description={
          isLiveCatalog
            ? product?.provider === "oxylabs" || product?.oxylabs?.source || product?.data_quality?.provider === "oxylabs"
              ? "Live Walmart product data via Oxylabs (standard parser + supplemental analyzer preset). Multi-period history is never invented."
              : "Live Walmart product data. Multi-period history is never invented or reused from demo products."
            : isDemoRequest
              ? "Optional demo fixture — not live catalog data. Use Analyze from the home screen for live Walmart lookups."
              : "Unified source → Walmart workspace with a deterministic verdict."
        }
      />
      {/* ===== STICKY PRODUCT HEADER ===== */}
      <div className="sticky top-14 z-[15] -mx-4 px-4 pt-2 pb-2 bg-surface-1 border-b border-edge-subtle">
        <MatchHeader product={product} bundle={bundle} verdict={verdict} panelOpen={panelOpen} setPanelOpen={setPanelOpen} />
      </div>

      {/* ===== WORKSPACE: TABS + DECISION PANEL ===== */}
      <div className={cn("mt-4 grid gap-4 min-w-0 grid-cols-1", panelOpen ? "lg:grid-cols-[minmax(0,1fr)_340px]" : "lg:grid-cols-1")}>
        {/* LEFT — tabbed content */}
        <div className="min-w-0">
          <div
            ref={tabListRef}
            className="relative flex items-center gap-1 border-b border-edge-subtle mb-4 overflow-x-auto ez-scroll"
            role="tablist"
            aria-label="Analyzer sections"
          >
            {TABS.map((t) => {
              const Icon = t.icon; const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  ref={(el) => { if (el) tabBtnRefs.current[t.key] = el; else delete tabBtnRefs.current[t.key]; }}
                  role="tab"
                  aria-selected={active}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "relative flex items-center gap-1.5 px-3 h-10 text-compact whitespace-nowrap transition-colors",
                    active ? "text-emerald-600 font-semibold" : "text-ink-muted hover:text-ink-secondary",
                  )}
                >
                  <Icon className="h-4 w-4" />{t.label}
                </button>
              );
            })}
            <span
              aria-hidden="true"
              data-testid="tab-indicator"
              className="pointer-events-none absolute -bottom-px left-0 h-0.5 rounded-t bg-emerald-500 motion-safe:transition-[transform,width] motion-safe:duration-300 motion-safe:ease-out"
              style={{
                width: tabIndicator.width,
                transform: `translateX(${tabIndicator.left}px)`,
              }}
            />
          </div>

          {tab === "overview" && (
            <div className="space-y-4 min-w-0 ez-rise-in" data-testid="tab-panel-overview">
              <div className="grid gap-4 sm:grid-cols-2 min-w-0" data-testid="demand-estimate-cards">
                <EstimateMetricCard
                  testid="est-monthly-sales"
                  heading="EST. MONTHLY SALES"
                  value={formatUnitsRange(
                    product?.demand_estimate?.monthly_units_low,
                    product?.demand_estimate?.monthly_units_high,
                    num,
                  )}
                  caption="units/month"
                  tip={demandTooltip(product?.demand_estimate)}
                />
                <EstimateMetricCard
                  testid="est-monthly-revenue"
                  heading="EST. MONTHLY REVENUE"
                  value={formatMoneyRange(
                    product?.revenue_estimate?.monthly_revenue_low,
                    product?.revenue_estimate?.monthly_revenue_high,
                    usd,
                  )}
                  caption="Estimated monthly revenue"
                  tip={revenueTooltip(product?.revenue_estimate, product?.demand_estimate)}
                />
              </div>

              <Card
                title="Current, Range & Competition"
                testid="current-average"
                badge={!isLiveCatalog}
                right={<span className="text-micro text-ink-muted">{isLiveCatalog ? "Snapshot only — no invented history" : "Keepa-style history"}</span>}
              >
                {bundleLiveEmpty || !caRows.length ? (
                  <UnavailableBlock
                    testid="current-average-unavailable"
                    title="Snapshot not available"
                    message="There is no current product snapshot for this listing yet — Current, Range & Competition cannot be filled from another product's demo numbers."
                    reason={bundle?.current_average_reason || bundle?.reasons?.current_average || (isLiveCatalog ? "history_unavailable" : null)}
                  />
                ) : (
                <CurrentRangeCompetitionTable rows={caRows} />
                )}
              </Card>

              <Card title="Competition" testid="competition-snapshot">
                  {Object.entries({
                    "Walmart presence": product.competition.walmart_presence == null ? "—" : product.competition.walmart_presence ? "Yes" : "No",
                    "Featured Offer seller": product.competition.buy_box_owner ?? "—",
                    "Sold & shipped by": product.competition.sold_and_shipped_by ?? "—",
                    "Fulfillment": product.competition.fulfillment_label
                      || product.fulfillment_classification?.label
                      || product.competition.fulfillment_classification?.label
                      || "—",
                    "Seller count": product.competition.seller_count ?? product.competition.total_offers ?? "—",
                    "Detailed offers": product.competition.observed_offer_count ?? "—",
                    "Additional sellers": product.competition.additional_seller_count ?? "—",
                    "Offers from": product.competition.additional_offers_starting_price != null
                      ? usd(product.competition.additional_offers_starting_price)
                      : (product.competition.additional_offers_starting_price_text || "—"),
                    "WFS offers": product.competition.wfs_offers ?? "—",
                    "Seller concentration": product.competition.seller_concentration ?? "—",
                    "Price gap": product.competition.price_gap == null ? "—" : usd(product.competition.price_gap),
                    "Shipping": product.competition.shipping_promise ?? "—",
                    "Delivery": product.competition.delivery_promise ?? "—",
                    "Pickup": product.competition.pickup_promise ?? "—",
                  }).map(([k, v]) => {
                    const fulfillmentTip = k === "Fulfillment" && (
                      product.fulfillment_classification?.is_estimate
                      || product.competition.fulfillment_classification?.is_estimate
                    )
                      ? `Estimated classification (${(product.fulfillment_classification || product.competition.fulfillment_classification)?.type}). Signals: ${((product.fulfillment_classification || product.competition.fulfillment_classification)?.basis || []).join(", ") || "n/a"}.`
                      : null;
                    return (
                      <div key={k} className="flex items-center justify-between gap-2 text-compact py-1 border-b border-edge-subtle last:border-0">
                        <span className="text-ink-secondary min-w-0 truncate inline-flex items-center gap-1">
                          {k}
                          {fulfillmentTip && (
                            <span title={fulfillmentTip} tabIndex={0} aria-label={fulfillmentTip} className="cursor-help">ⓘ</span>
                          )}
                        </span>
                        <span className="font-mono text-ink-primary shrink-0">{v}</span>
                      </div>
                    );
                  })}
                  {(product.competition.total_offers_note || product.competition.note) && (
                    <p className="text-micro text-ink-muted mt-2">{product.competition.total_offers_note || product.competition.note}</p>
                  )}
                  {isLiveCatalog && !product?.unwrangle?.source && !product?.oxylabs?.source && (
                    <p className="text-micro text-ink-muted mt-2">
                      Offer counts and buy-box sellers are not returned by catalog search; only 1P flag when present.
                    </p>
                  )}
                  {(product?.oxylabs?.source || product?.unwrangle?.source) && (
                    <p className="text-micro text-ink-muted mt-2" data-testid="seller-aggregate-note">
                      {`${product.competition.observed_offer_count ?? 1} seller observed directly. Walmart reports ${product.competition.additional_seller_count ?? 0} additional seller options.`}
                    </p>
                  )}
                </Card>

              <BuySourcesPanel
                result={buySourcesResult}
                loading={buySourcesLoading}
                searched={buySourcesSearched}
                onFindSources={handleFindBuySources}
              />
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-4 min-w-0 ez-rise-in" data-testid="tab-panel-history">
              <MarketHistory history={history} loading={!history} />
              <HistoricalComparison bundle={bundle} liveMode={isLiveCatalog} history={history} />
              <RoiTargetTable
                input={input}
                calc={calc}
                bundle={bundle}
                liveMode={isLiveCatalog}
                bundleLiveEmpty={bundleLiveEmpty}
              />
            </div>
          )}

          {tab === "sellers" && (
            <div className="space-y-4 min-w-0 ez-rise-in" data-testid="tab-panel-sellers">
              <Card title="Marketplace Offers" testid="offers-table"
                right={!bundleLiveEmpty && <div className="inline-flex items-center gap-0.5 p-0.5 rounded-ctrl border border-edge-subtle text-micro font-mono">
                  {[["all", "All"], ["wfs", "WFS"], ["sf", "Seller"], ["retail", "Walmart"]].map(([k, l]) => (
                    <button key={k} onClick={() => setOfferFilter(k)} data-testid={`offer-filter-${k}`}
                      className={cn("px-2.5 h-7", offerFilter === k ? "ez-seg-active" : "ez-seg")}>{l}</button>
                  ))}
                </div>}>
                {bundleLiveEmpty || !offers.length ? (
                  <UnavailableBlock
                    testid="sellers-offers-unavailable"
                    title="Seller list not available"
                    message="Catalog search does not return competing 3P offers. Showing demo sellers next to a live listing would be misleading."
                    reason={bundle?.offers_reason || "offers_unavailable"}
                  />
                ) : (
                <>
                {(bundle?.seller_count > offers.length || bundle?.offers_note || product?.competition?.additional_seller_count > 0) && (
                  <p className="text-micro text-ink-muted mb-2 px-1" data-testid="offers-aggregate-note">
                    {`${bundle?.observed_offer_count ?? product?.competition?.observed_offer_count ?? offers.length} seller observed directly. Walmart reports ${bundle?.additional_seller_count ?? product?.competition?.additional_seller_count ?? 0} additional seller options.`}
                    {(bundle?.seller_count ?? product?.competition?.seller_count) != null && (
                      <> Total sellers {(bundle?.seller_count ?? product?.competition?.seller_count)}.</>
                    )}
                    {bundle?.additional_offers_starting_price != null && (
                      <> Offers from {usd(bundle.additional_offers_starting_price)}.</>
                    )}
                  </p>
                )}
                <div className="overflow-x-auto ez-scroll">
                  <table className="w-full text-compact">
                    <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
                      <th className="py-2 px-3">Seller</th><th className="px-3">Fulfillment</th><th className="px-3 text-right">Rating</th>
                      <th className="px-3 text-right">Item price</th><th className="px-3 text-right">Shipping</th><th className="px-3 text-right">Delivered</th>
                      <th className="px-3 text-right">Est. profit</th><th className="px-3 text-right">ROI</th><th className="px-3">Featured</th><th className="px-3">Last observed</th>
                    </tr></thead>
                    <tbody>
                      {offers.map((o, i) => (
                        <tr key={i} className="border-b border-edge-subtle last:border-0 hover:bg-surface-2">
                          <td className="py-2 px-3 text-ink-primary">{o.seller}</td>
                          <td className="px-3 text-ink-secondary">
                            <span className="inline-flex items-center gap-1">
                              {o.fulfillment}
                              {o.fulfillment_classification?.is_estimate && (
                                <span
                                  title={`Estimated (${o.fulfillment_classification.type}). ${(o.fulfillment_classification.basis || []).join(", ")}`}
                                  tabIndex={0}
                                  className="cursor-help text-ink-muted"
                                >
                                  ⓘ
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-3 text-right font-mono">{o.rating}</td><td className="px-3 text-right font-mono">{usd(o.item_price)}</td>
                          <td className="px-3 text-right font-mono">{usd(o.shipping)}</td><td className="px-3 text-right font-mono">{usd(o.delivered)}</td>
                          <td className="px-3 text-right font-mono">{usd(o.est_profit)}</td>
                          <td className={cn("px-3 text-right font-mono", o.roi >= 30 ? "text-emerald-600" : "text-amber-600")}>{pct(o.roi)}</td>
                          <td className="px-3">{o.featured ? <span className="text-micro font-mono bg-signal-blue/10 text-signal-blue rounded px-1.5 py-0.5">Featured</span> : ""}</td>
                          <td className="px-3 text-micro text-ink-muted">{ago(o.last_observed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}
              </Card>
            </div>
          )}

          {tab === "data" && (
            <div className="min-w-0 ez-rise-in" data-testid="tab-panel-data">
              <WalmartDataTab product={product} bundle={bundle} history={history} input={input} calc={calc} setField={setField} liveMode={isLiveCatalog} />
            </div>
          )}

          {tab === "variations" && (
            <div className="min-w-0 ez-rise-in" data-testid="tab-panel-variations">
              {bundleLiveEmpty || !(bundle?.variations || []).length ? (
                <Card title="Variations" testid="variations-unavailable-card">
                  <UnavailableBlock
                    testid="variations-unavailable"
                    title="Variations not available"
                    message="Sibling style/size variations require catalog variation data we don't have for this item."
                    reason={bundle?.reasons?.variations || "variations_unavailable"}
                  />
                </Card>
              ) : (
              <WalmartVariationsTab bundle={bundle} product={product}
                onLoadVariation={(id) => toast(`Loaded variation ${id} into analyzer (fixture)`)}
                onSaveLead={(id) => { void saveLead(id); }}
                onAddToPurchaseList={(ids) => toast(`Added ${ids.length} variation(s) to purchase list (fixture)`)} />
              )}
            </div>
          )}

          {tab === "details" && (
            <div className="min-w-0 ez-rise-in" data-testid="tab-panel-details">
              <WalmartProductDetailsTab bundle={bundle} product={product} liveMode={isLiveCatalog} />
            </div>
          )}
        </div>

        {/* RIGHT — sticky decision panel */}
        {panelOpen && (
          <aside className="min-w-0 hidden lg:block" data-testid="decision-panel">
            <div className="sticky top-[116px] space-y-4">
              <Panel className="overflow-hidden min-w-0" testid="verdict-panel">
                <div className="flex min-w-0">
                  <div className={cn("w-1.5 shrink-0", vs.rail)} />
                  <div className="p-4 flex-1 min-w-0">
                    {verdict.verdict === "INCOMPLETE" && !(Number(input.acquisition_cost) > 0) ? (
                      <div data-testid="purchase-cost-needed">
                        <p className="text-compact font-semibold text-ink-primary">
                          Purchase cost needed to calculate profit
                        </p>
                        <p className="mt-1 text-micro text-ink-secondary">
                          Enter your acquisition cost below. Profit and ROI update automatically — missing cost does not mean the product is profitable.
                        </p>
                        <label className="mt-3 block">
                          <span className="text-micro font-mono uppercase text-ink-muted">Acquisition cost</span>
                          <input
                            data-testid="decision-acquisition-cost"
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            className="mt-1 w-full h-9 px-2.5 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact font-mono text-ink-primary"
                            placeholder="0.00"
                            value={input.acquisition_cost ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setField("acquisition_cost", raw === "" ? null : Number(raw));
                            }}
                          />
                        </label>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          {verdict.verdict !== "INCOMPLETE" && (
                            <VerdictBadge verdict={verdict.verdict} />
                          )}
                          {verdict.verdict === "INCOMPLETE" && (
                            <span className="text-compact font-semibold text-ink-primary" data-testid="incomplete-inputs-label">
                              More inputs needed
                            </span>
                          )}
                          <span className="text-micro font-mono text-ink-muted">{new Date().toLocaleTimeString()}</span>
                        </div>
                        <p className={cn("mt-2 text-compact", vs.text)}>{verdict.headline}</p>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-micro font-mono text-ink-secondary">
                          <span className="text-emerald-600">{verdict.counts.pass || 0} pass</span>
                          <span className="text-amber-600">{verdict.counts.warning || 0} warn</span>
                          <span className="text-red-600">{verdict.counts.fail || 0} fail</span>
                          <span>Conf {input.confidence}%</span>
                        </div>
                        <button data-testid="view-logic-btn" onClick={() => setShowLogic(true)} className="mt-3 text-compact text-signal-blue flex items-center gap-1">
                          <Eye className="h-4 w-4" /> View decision logic
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </Panel>

              <ProfitCalculator
                input={input}
                calc={calc}
                product={product}
                fulfillment={fulfillment}
                onFulfillmentChange={changeFulfillment}
                setField={setField}
                onReset={() => setInput({ ...product.calc_input })}
                advanced={advanced}
                setAdvanced={setAdvanced}
                hideZero={hideZero}
                setHideZero={setHideZero}
              />
            </div>
          </aside>
        )}
      </div>

      {/* ===== STICKY ACTION BAR ===== */}
      <div className="fixed md:sticky bottom-14 md:bottom-4 left-0 right-0 md:left-auto md:right-auto z-20 px-4 md:px-0 mt-4">
        <Panel className="p-2.5 flex items-center justify-between gap-2 shadow-menu max-w-[1720px] mx-auto min-w-0">
          <button onClick={() => toast("Suggested test qty 24 — capped by $600 budget, MOQ 12 and conservative demand proxy", { duration: 6000 })}
            className="hidden sm:flex items-center gap-1 text-compact text-ink-secondary">
            <HelpCircle className="h-4 w-4" /> Suggested qty <b className="font-mono text-ink-primary">24</b> · Why?
          </button>
          <div className="hidden sm:flex items-center gap-2 ml-auto">
            <button data-testid="action-reject" onClick={() => setConfirmReject(true)} className="h-9 px-3 rounded-ctrl border border-red-600/40 text-red-600 text-compact hover:bg-red-100"><X className="h-4 w-4 inline mr-1" />Reject</button>
            <button data-testid="action-watch" onClick={() => toast("Added to Watchlist (fixture)")} className="h-9 px-3 rounded-ctrl border border-amber-600/40 text-amber-600 text-compact hover:bg-amber-100"><Eye className="h-4 w-4 inline mr-1" />Watch</button>
            <button data-testid="action-save-lead" disabled={savingLead || !product} onClick={() => { void saveLead(); }} className="h-9 px-3 rounded-ctrl bg-emerald-600 text-white text-compact hover:bg-emerald-500 ez-glow-emerald disabled:opacity-50"><Bookmark className="h-4 w-4 inline mr-1" />{savingLead ? "Saving…" : "Save Lead"}</button>
            <button data-testid="action-purchase-list" onClick={() => toast("Added to purchase list (fixture)")} className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2"><ListPlus className="h-4 w-4 inline mr-1" />Purchase list</button>
          </div>
          <div className="flex sm:hidden items-center gap-2 w-full">
            <button data-testid="action-save-lead-m" disabled={savingLead || !product} onClick={() => { void saveLead(); }} className="flex-1 h-11 rounded-ctrl bg-emerald-600 text-white text-compact ez-glow-emerald disabled:opacity-50"><Bookmark className="h-4 w-4 inline mr-1" />{savingLead ? "Saving…" : "Save"}</button>
            <button data-testid="action-watch-m" onClick={() => toast("Watching (fixture)")} className="flex-1 h-11 rounded-ctrl border border-amber-600/40 text-amber-600 text-compact"><Eye className="h-4 w-4 inline mr-1" />Watch</button>
            <div className="relative">
              <button data-testid="action-more" aria-label="More actions" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)} className="h-11 w-11 grid place-items-center rounded-ctrl border border-edge-subtle"><MoreHorizontal className="h-5 w-5" /></button>
              {moreOpen && (
                <div className="absolute bottom-12 right-0 w-44 bg-surface-0 border border-edge-subtle rounded-panel shadow-menu py-1">
                  <button data-testid="more-reject" onClick={() => { setMoreOpen(false); setConfirmReject(true); }} className="w-full text-left px-3 py-2 text-compact text-red-600 hover:bg-surface-2">Reject…</button>
                  <button data-testid="more-purchase-list" onClick={() => { setMoreOpen(false); toast("Added to purchase list (fixture)"); }} className="w-full text-left px-3 py-2 text-compact hover:bg-surface-2">Add to Purchase list</button>
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* Reject confirm */}
      {confirmReject && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 grid place-items-center p-4" onClick={() => setConfirmReject(false)}>
          <div role="dialog" aria-modal="true" aria-label="Reject product" className="bg-surface-0 rounded-panel shadow-menu p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="reject-confirm">
            <div className="flex items-center gap-2 text-red-600"><MinusCircle className="h-5 w-5" /><span className="text-stitle">Reject this product?</span></div>
            <p className="text-compact text-ink-secondary mt-2">It will be marked rejected in scan history (fixture). You can re-analyze it later.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmReject(false)} className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact">Cancel</button>
              <button onClick={() => { setConfirmReject(false); toast.error("Rejected (fixture)"); }} className="h-9 px-3 rounded-ctrl bg-red-600 text-white text-compact">Reject</button>
            </div>
          </div>
        </div>, document.body)}

      {/* Decision logic drawer */}
      {showLogic && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 flex" onClick={() => setShowLogic(false)}>
          <div role="dialog" aria-modal="true" aria-label="Decision logic" className="ml-auto h-full w-full max-w-lg ez-glass-strong flex flex-col ez-rise-in" onClick={(e) => e.stopPropagation()} data-testid="decision-logic">
            <div className="h-14 flex items-center justify-between px-4 border-b border-edge-subtle bg-surface-0">
              <div className="font-medium text-ink-primary flex items-center gap-2">Decision logic <VerdictBadge verdict={verdict.verdict} /></div>
              <button data-testid="close-logic-btn" autoFocus onClick={() => setShowLogic(false)} aria-label="Close decision logic" className="text-ink-muted h-8 w-8 grid place-items-center">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto ez-scroll p-4 space-y-2">
              {verdict.rules.map((rl, i) => (
                <div key={i} className="rounded-ctrl border border-edge-subtle p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-compact font-medium text-ink-primary">{rl.name}</span>
                    <StatusPill status={rl.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1 text-micro font-mono text-ink-secondary">
                    <span>Threshold: {rl.threshold}</span><span>Observed: {rl.observed}</span>
                    <span>Affects verdict: {rl.affects ? "yes" : "no"}</span><span>Confidence: {input.confidence}%</span>
                    <span>Source: {rl.source}</span><span><ProvenanceTag kind="fixture" /></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}

const HC_ROWS = [
  ["Featured Offer", "Featured Offer price", "money"],
  ["Walmart.com price", null, "money"],
  ["Lowest WFS", "Lowest WFS", "money"],
  ["Lowest seller-fulfilled", "New (lowest)", "money"],
  ["Estimated demand", null, "count"],
  ["Total offers", "New offer count", "count"],
  ["Seller count", "Seller count", "count"],
  ["WFS offers", null, "count"],
  ["Walmart Retail presence", null, "pct"],
];

function UnavailableBlock({ title, message, reason, testid }) {
  return (
    <div className="py-8 px-2 text-center" data-testid={testid || "section-unavailable"}>
      <div className="text-compact font-medium text-ink-primary">{title || "Not available"}</div>
      {message && <p className="text-compact text-ink-secondary mt-1 max-w-lg mx-auto">{message}</p>}
      {reason && <p className="text-micro font-mono text-ink-muted mt-2">{reason}</p>}
    </div>
  );
}

function RoiTargetTable({ input, calc, bundle, liveMode = false, bundleLiveEmpty = false }) {
  const scenarios = useMemo(() => computeRoiScenarios(input), [input]);
  const fixtureRows = !liveMode && (bundle?.roi_targets || []).length ? bundle.roi_targets : null;
  const rows = scenarios.available ? scenarios.rows : fixtureRows;

  if (!rows || !rows.length) {
    const need = scenarios.missing?.length
      ? `Enter ${scenarios.missing.join(" and ")} in the Profit Calculator to derive break-even and ROI sale targets.`
      : "ROI scenarios need acquisition cost and referral rate assumptions.";
    return (
      <Card title="ROI Target Table" testid="roi-targets">
        <UnavailableBlock
          testid="roi-targets-unavailable"
          title="ROI scenarios need inputs"
          message={need}
          reason={scenarios.missing?.join(", ") || bundle?.reasons?.roi_targets || null}
        />
      </Card>
    );
  }

  return (
    <Card title="ROI Target Table" testid="roi-targets"
      right={<span className="text-micro text-ink-muted">{scenarios.available ? "Calculated scenarios — not observed prices" : "Demo"}</span>}>
      {scenarios.available && scenarios.break_even_price != null && (
        <p className="text-compact text-ink-secondary mb-3 px-1" data-testid="roi-break-even">
          Break-even sale price: <span className="font-mono font-semibold text-ink-primary">{usd(scenarios.break_even_price)}</span>
        </p>
      )}
      <div className="overflow-x-auto ez-scroll">
        <table className="w-full text-compact">
          <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
            <th className="py-2 px-3 text-right">Target ROI</th><th className="px-3 text-right">Required sale</th>
            <th className="px-3 text-right">Max acquisition</th><th className="px-3 text-right">Expected profit</th><th className="px-3">Feasible</th>
          </tr></thead>
          <tbody>
            {rows.map((t, i) => (
              <tr key={i} className="border-b border-edge-subtle last:border-0">
                <td className="py-1.5 px-3 text-right font-mono">{t.target_roi}%</td>
                <td className="px-3 text-right font-mono">{usd(t.required_sale)}</td>
                <td className="px-3 text-right font-mono">{t.max_acquisition == null ? "—" : usd(t.max_acquisition)}</td>
                <td className="px-3 text-right font-mono">{usd(t.expected_profit)}</td>
                <td className="px-3">
                  {t.feasible == null ? <span className="text-ink-muted">—</span> : <StatusPill status={t.feasible ? "pass" : "fail"} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {scenarios.available && (
        <p className="text-micro text-ink-muted mt-2">
          Hypothetical selling prices for selected ROI targets using your acquisition cost and fee assumptions. Not historical market prices.
        </p>
      )}
    </Card>
  );
}

function HistoricalComparison({ bundle, liveMode = false, history = null }) {
  const [period, setPeriod] = useState("90d");
  const obsCount = history?.observation_count ?? history?.series?.length ?? 0;
  const hasRealAverages = !!(bundle?.current_average?.rows || []).length && !liveMode;
  const insufficientLive = liveMode && obsCount < 2;

  const unavailable =
    insufficientLive ||
    (!hasRealAverages && (liveMode || bundle?.available === false || bundle?.current_average_reason === "history_unavailable" || !(bundle?.current_average?.rows || []).length));

  if (unavailable) {
    return (
      <Card title="Historical Comparison" testid="historical-comparison">
        <UnavailableBlock
          testid="historical-comparison-unavailable"
          title="Not enough observations for period averages"
          message="30D / 90D / 180D averages appear only after enough product-specific price observations exist. We never invent or reuse another product's averages."
          reason={obsCount ? `observations=${obsCount}` : (bundle?.current_average_reason || "history_unavailable")}
        />
      </Card>
    );
  }

  const rowsById = {};
  (bundle?.current_average?.rows || []).forEach((r) => { rowsById[r.metric] = r; });
  const colKey = { average30d: "30d", average90d: "90d", average180d: "180d", average365d: "365d" }[comparisonColumnForPeriod(period)];
  const COLS = [
    { id: "current", label: "Current", vi: 0 }, { id: "30d", label: "30d avg", vi: null },
    { id: "90d", label: "90d avg", vi: 1 }, { id: "180d", label: "180d avg", vi: 2 },
    { id: "365d", label: "365d avg", vi: 3 }, { id: "lowest", label: "Lowest", vi: 4 },
    { id: "highest", label: "Highest", vi: 5 }, { id: "changes", label: "Changes", vi: null },
    { id: "lastchange", label: "Last change", vi: null },
  ];
  return (
    <Card title="Historical Comparison" testid="historical-comparison"
      right={<div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro" role="group" aria-label="Comparison period">{["30d", "90d", "180d", "1y"].map((p) => (<button key={p} data-testid={`hc-period-${p}`} aria-pressed={period === p} onClick={() => setPeriod(p)} className={cn("px-2 h-6", period === p ? "ez-seg-active" : "ez-seg")}>{p.toUpperCase()}</button>))}</div>}>
      <div className="overflow-x-auto ez-scroll">
        <table className="w-full text-compact" style={{ minWidth: 900 }} data-testid="historical-comparison-table">
          <thead><tr className="text-micro text-ink-muted text-left bg-surface-2">
            <th className="px-3 py-2 border-b border-edge-subtle">Metric</th>
            {COLS.map((c) => (<th key={c.id} className={cn("px-3 py-2 border-b border-edge-subtle text-right whitespace-nowrap", c.id === colKey && "bg-emerald-100/60 text-emerald-700")}>{c.label}</th>))}
          </tr></thead>
          <tbody>
            {HC_ROWS.map(([label, metric, unit], i) => {
              const row = metric ? rowsById[metric] : null;
              return (
                <tr key={i} className="hover:bg-surface-1">
                  <td className="px-3 py-2 border-b border-edge-subtle text-ink-primary whitespace-nowrap">{label}</td>
                  {COLS.map((c) => { const v = row && c.vi != null ? row.values[c.vi] : null; return (<td key={c.id} className={cn("px-3 py-2 border-b border-edge-subtle text-right tabnum", c.id === colKey && "bg-emerald-100/30")}>{v == null ? "—" : fmtCA(v, unit)}</td>); })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-micro text-ink-muted mt-2">Each column is computed within its exact window; unavailable windows show —. Demo path only. Selected period highlights the matching column.</p>
    </Card>
  );
}
